import { useEffect, useRef } from "react";
import { Button, Input, TextArea } from "../../RequestRespectingInputs";
import { Valid } from "../../CommonComponents";
import { HugeRTEComponent } from "./HugeRTEComponent";
import { BookEditExplanations } from "./explanation";
import { useAppDispatch } from "../../../store/hooks";
import { bookEditorActions } from "./slice";
import { Book } from "../../../rest/models";
import { recalculateBlocksAndSentences } from "../utils";
import { makePlainObject } from "../../../utils";

export function BookBasicOptionsEdit(props: Props)
{
    const appDispatch = useAppDispatch();
    const sentencesNeedRecomputingRef = useRef(false);

    const { formBook } = props;
    const formBookRef = useRef(props.formBook);
    formBookRef.current = props.formBook;
    
    //Create a memoized callback. The HugeRTE editor is created once per page,
    //so it needs a callback that's also created once per page.
    const handleSourceTextChange = useRef(function(newValue: string)
    {
        appDispatch(bookEditorActions.setSourceText(newValue));
    });

    //The next few functions make sure that we recalculate the sentences when text changes, and the editor loses focus.
    const handleSourceTextBlur = useRef(function()
    {
        if (sentencesNeedRecomputingRef.current)
        {
            recalculateBlocksAndSentences(formBookRef.current, appDispatch);
        }
    });
    
    useEffect(function()
    {
        //Text changed.
        //(Or component just mounted.)
        sentencesNeedRecomputingRef.current = true;
    }, [formBook.source_text]);
    
    useEffect(function()
    {
        //Sentences HAVE been recomputed.
        //(Or component just mounted. Cancels out the above in that case, which is good.)
        sentencesNeedRecomputingRef.current = false;
    }, [formBook.sentence_set]);
    
    function handleIsHTMLChange(checked: boolean)
    {
        const book = makePlainObject(formBook);
        book.is_html = checked;
        
        if (checked)
        {
            //Change the previously plain text to look right in the RTE.
            const tempElement = document.createElement('div');
            tempElement.textContent = formBook.source_text;
            book.source_text = tempElement.innerHTML.replaceAll('\n', '<br/>');
        }
        else
        {
            //Change the previously rich text to look right in the textarea.
            const tempElement = document.createElement('div');
            tempElement.innerHTML = props.formBook.source_text;

            // Replace <br> elements with newline characters
            tempElement.querySelectorAll('br').forEach(function(br)
            {
                br.replaceWith('\n');
            });
            
            book.source_text = tempElement.textContent ?? '';
        }

        recalculateBlocksAndSentences(book, appDispatch);
    }

    function collapseSingleNewlines(sourceText: string, appDispatch: ReturnType<typeof useAppDispatch>)
    {
        if (formBook.is_html)
        {
            if (!window.confirm('Collapse single newlines? This operation removes any formatting in the document.'))
            {
                return;
            }
            const tempElm = document.createElement('div');
            document.body.appendChild(tempElm);

            tempElm.innerHTML = sourceText;
            const innerText = tempElm.innerText; //Not .textContent!
            const collapsed = innerText.replaceAll(/\n{2,}/g, '<br/><br/>').trim();
            document.body.removeChild(tempElm);

            appDispatch(bookEditorActions.setSourceText(collapsed));
        }
        else
        {
            if (!window.confirm('Collapse single newlines?'))
            {
                return;
            }

            const collapsed = sourceText.replaceAll(/\n+/g, (match) => match === "\n" ? ' ' : match);
            appDispatch(bookEditorActions.setSourceText(collapsed));
        }
    }

    return (
        <>
            <p>Let's create your book! Start by giving it a title and setting its source text.</p>

            <label htmlFor='title'>Title</label>
            <Valid bValid={formBook.title.trim()} children='A title is required.'/>
            <Input
                id="title"
                type="text"
                maxLength={1024}
                value={formBook.title}
                onChange={(e) => appDispatch(bookEditorActions.setTitle(e.target.value))}/>
            <br/><br/>

            <label htmlFor='is_html'>Use Rich Text Editor</label>
            <Input
                id="is_html"
                type="checkbox"
                checked={formBook.is_html}
                onChange={(e) => handleIsHTMLChange(e.target.checked)}/>
                
            {formBook.is_html ? ' (Warning: switching from rich to plain text can lead to loss of formatting.)' : ''}
            <br/><br/>

            <label htmlFor='source_text'>Source Text</label>
            <Valid bValid={formBook.source_text.trim()} children='Source text is required.'/>
            {formBook.is_html
                ? <HugeRTEComponent
                    value={formBook.source_text}
                    onChange={handleSourceTextChange.current}
                    onBlur={handleSourceTextBlur.current}
                    id="source_text" />
                : <TextArea
                    value={formBook.source_text}
                    onChange={(e) => handleSourceTextChange.current(e.target.value)}
                    onBlur={handleSourceTextBlur.current}
                    id="source_text" />
            }
            <br/><br/>

            <label htmlFor='collapse_single_newlines'>Collapse single newlines</label>
            <div>{BookEditExplanations.collapse_single_newlines}</div>
            {formBook.is_html
                ? <div className='ernots_Warning'>Removes all formatting.</div>
                : <></>}

            <Button
                id="collapse_single_newlines"
                onClick={(e) => collapseSingleNewlines(formBook.source_text, appDispatch)}
                children='Collapse single newlines'/>
            
        </>
    );
}

interface Props
{
    formBook: Book;
}